import React from 'react';
import Icon, {
  ApartmentOutlined,
  BranchesOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  CopyOutlined,
  ExperimentOutlined,
  EyeOutlined,
  ForkOutlined,
  FormOutlined,
  HeartOutlined,
  HistoryOutlined,
  HourglassOutlined,
  InfoCircleOutlined,
  MedicineBoxOutlined,
  RobotOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';

import {
  PRACTITIONER_TASK_ICON_MAP,
  PractitionerTaskType,
  STAGE_CONFIG,
  STAGE_TYPE_ICON_MAP,
  StageType,
} from '../../types/healthcare/TreatmentPlanTypes';

/**
 * SINGLE SOURCE OF TRUTH for treatment-plan stage / practitioner-task icons.
 *
 * The string→icon name mapping lives in TreatmentPlanTypes.ts (STAGE_TYPE_ICON_MAP,
 * PRACTITIONER_TASK_ICON_MAP); this module maps those names to the actual AntD components once and
 * exposes the helpers every surface consumes — the runtime stage list (getStageIcon, via helpers),
 * the builder canvas nodes and the builder palette. Do not reintroduce a local ICONS map in those
 * components; add the icon here instead.
 */
const ICON_COMPONENTS: Record<string, React.ComponentType<any>> = {
  ApartmentOutlined,
  BranchesOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  CopyOutlined,
  ExperimentOutlined,
  EyeOutlined,
  ForkOutlined,
  FormOutlined,
  HeartOutlined,
  HistoryOutlined,
  HourglassOutlined,
  InfoCircleOutlined,
  MedicineBoxOutlined,
  RobotOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  UserOutlined,
};

const FALLBACK_ICON = 'InfoCircleOutlined';

/**
 * Resolve the icon-name string for a stage. Practitioner-task stages resolve to their task-type
 * icon when a task type is supplied, so a stage shows what it actually does.
 */
export const resolveStageIconName = (type: StageType, taskType?: PractitionerTaskType): string => {
  if (type === 'practitioner_task' && taskType && PRACTITIONER_TASK_ICON_MAP[taskType]) {
    return PRACTITIONER_TASK_ICON_MAP[taskType];
  }
  return STAGE_TYPE_ICON_MAP[type] ?? FALLBACK_ICON;
};

const getComponent = (name: string): React.ComponentType<any> =>
  ICON_COMPONENTS[name] ?? ICON_COMPONENTS[FALLBACK_ICON];

/* ── Runtime actor icons (plan modal stage list + dashboard) ──────────────────
 * In the runtime views every stage collapses to one of TWO icons based on WHO performs / interacts
 * with it: a practitioner (clinician/stethoscope) or the patient (person). e.g. a stage waiting on a
 * patient form shows the patient icon; a practitioner task shows the practitioner icon. The builder
 * (getStageNodeIcon, below) still uses the distinct per-type icons so authors can tell nodes apart.
 */

export type StageActor = 'practitioner' | 'patient';

/** Who performs / interacts with each stage type. */
export const STAGE_ACTOR: Record<StageType, StageActor> = {
  trigger:                'practitioner', // system; hidden in the runtime list
  action:                 'patient',      // assigns forms / sends notifications to the patient
  condition:              'practitioner', // system; hidden
  time_condition:         'practitioner', // system; hidden
  watcher:                'patient',      // waits on the patient (e.g. completing a form)
  manual:                 'practitioner',
  sub_plan_selection:     'practitioner',
  appointment:            'patient',
  practitioner_task:      'practitioner',
  patient_analysis:       'practitioner', // practitioner reviews the AI guidance
  practitioner_option:    'practitioner',
  comprehensive_analysis: 'practitioner', // practitioner reviews the analysis
  admin_review:           'practitioner', // admin-only; display-only in the clinic (read-only frozen state)
};

export const getStageActor = (type: StageType): StageActor => STAGE_ACTOR[type] ?? 'practitioner';

/** Clinician glyph (stethoscope) — AntD has no medical-person icon, so this is a custom SVG. */
const StethoscopeSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
    <path d="M19,8C19.56,8 20,8.43 20,9A1,1 0 0,1 19,10C18.43,10 18,9.56 18,9C18,8.43 18.43,8 19,8M2,2V11C2,13.96 4.19,16.5 7.14,16.91C7.76,19.92 10.42,22 13.5,22A6.5,6.5 0 0,0 20,15.5V11.81C21.16,11.39 22,10.29 22,9A3,3 0 0,0 19,6A3,3 0 0,0 16,9C16,10.29 16.84,11.39 18,11.81V15.5C18,17.97 16,20 13.5,20C11.5,20 9.82,18.71 9.22,16.92C12.12,16.47 14,13.95 14,11V2H10V5H12V11C12,13.21 10.21,15 8,15C5.79,15 4,13.21 4,11V5H6V2H2Z" />
  </svg>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PractitionerIcon: React.FC<any> = (props) => <Icon component={StethoscopeSvg} {...props} />;
const PatientIcon = UserOutlined;

/**
 * Runtime stage icon — resolves to the practitioner or patient actor icon. Keeps the historical
 * (type, size, taskType?) signature so callers don't change; taskType is no longer used to pick the
 * glyph (the actor is determined by stage type). Returns a single icon element so callers can still
 * cloneElement it with status colour / spin.
 */
export const getStageIcon = (
  type: StageType,
  size = 14,
  _taskType?: PractitionerTaskType,
): React.ReactElement => {
  const ActorIcon = getStageActor(type) === 'patient' ? PatientIcon : PractitionerIcon;
  return <ActorIcon style={{ fontSize: size, color: 'var(--mg-text-secondary)' }} />;
};

/**
 * Uncoloured icon element for the builder (canvas nodes + palette), which apply their own colour
 * on a wrapping element.
 */
export const getStageNodeIcon = (type: StageType, taskType?: PractitionerTaskType): React.ReactNode => {
  const Icon = getComponent(resolveStageIconName(type, taskType));
  return <Icon />;
};

/** Coloured icon for a practitioner task type on its own (uses the practitioner-task stage colour). */
export const getPractitionerTaskIcon = (taskType: PractitionerTaskType, size = 14): React.ReactElement => {
  const Icon = getComponent(PRACTITIONER_TASK_ICON_MAP[taskType] ?? FALLBACK_ICON);
  return <Icon style={{ fontSize: size, color: STAGE_CONFIG.practitioner_task.color }} />;
};
